/* eslint-disable no-redeclare */
import { useCallback, useEffect, useState } from "react";

type SetValue<T> = T | ((prevValue: T) => T);

/**
 * Custom hook that syncs state with localStorage
 * @param key - The localStorage key to use
 * @param initialValue - The initial value (used if no stored value exists)
 * @returns A stateful value and a function to update it, just like useState
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: SetValue<T>) => void];

export function useLocalStorage<T = undefined>(key: string): [T | undefined, (value: SetValue<T | undefined>) => void];

export function useLocalStorage<T>(
  key: string,
  initialValue?: T
): [T | undefined, (value: SetValue<T | undefined>) => void] {
  // Get initial value from localStorage or use provided initialValue
  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage
  const setValue = useCallback(
    (value: SetValue<T | undefined>) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;

        // Save state
        setStoredValue(valueToStore);

        // Save to localStorage
        if (typeof window !== "undefined") {
          if (valueToStore === undefined) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Listen for changes to this key from other tabs/windows
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue) as T);
        } catch (error) {
          console.warn(`Error parsing localStorage key "${key}":`, error);
        }
      } else if (e.key === key && e.newValue === null) {
        setStoredValue(undefined);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
}
